// Client-side API wrapper for all HornDeliver services.
// Phase 2: adds phone verification, advertising, commission, fraud, analytics.
// Phase 3: adds subscriptions, mobile money, chat translation, offline sync.
// Phase 5: adds CSRF token injection, auth flows (forgot/reset/change/refresh),
//          sessions, devices, GDPR erasure, audit logs, tenants.
import type {
  User,
  DeliveryJob,
  ChatMessage,
  Payment,
  Dispute,
  TrustRecord,
  Notification,
  Advertisement,
  CommissionRule,
  FraudFlag,
  SubscriptionPlan,
  Subscription,
  MobileMoneyAccount,
  MobileMoneyTransaction,
  MobileMoneyProvider,
  ServiceStatus,
  Store,
  Product,
  QuoteBreakdown,
  CourierEarnings,
  RoleApplication,
  Wallet,
  WalletTransaction,
  StoreBoost,
  UpgradeCalc,
} from "@/lib/types";

// Phase 5: CSRF — read the double-submit cookie and inject into the header
// for state-changing requests. The cookie is set by the server on first GET.
function getCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith("horndeliver_csrf="));
  return match ? match.split("=")[1] : null;
}

async function request<T>(
  url: string,
  options?: RequestInit
): Promise<{ ok: boolean; data?: T; error?: string }> {
  try {
    const method = (options?.method ?? "GET").toUpperCase();
    const isStateChanging = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options?.headers as Record<string, string>),
    };
    // Inject CSRF header for state-changing requests.
    if (isStateChanging) {
      const csrf = getCsrfCookie();
      if (csrf) headers["x-csrf-token"] = csrf;
    }
    const res = await fetch(url, { ...options, headers });
    // Auto-refresh on 419 (dedicated "session expired" status) — try refresh
    // then retry the original request once. We do NOT auto-refresh on 401
    // because 401 from login/register means wrong credentials, not an expired
    // session. The refresh endpoint returns 200 on success, 401 if no refresh
    // token exists, so it won't recurse.
    if (res.status === 419) {
      const refreshed = await tryAutoRefresh();
      if (refreshed) {
        const retryRes = await fetch(url, { ...options, headers });
        const retryJson = await retryRes.json().catch(() => ({}));
        if (retryRes.ok && retryJson.ok !== false) {
          return { ok: true, data: retryJson.data as T };
        }
      }
    }
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.ok === false) {
      return { ok: false, error: json.error || `Request failed (${res.status})` };
    }
    return { ok: true, data: json.data as T };
  } catch (e: any) {
    return { ok: false, error: e?.message || "Network error" };
  }
}

// Auto token refresh: calls /api/services/user/refresh to rotate the session.
let refreshPromise: Promise<boolean> | null = null;
async function tryAutoRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch("/api/services/user/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export const api = {
  // ---- USER SERVICE ----
  auth: {
    register: (body: { name: string; email: string; password: string; phone?: string; countryCode?: string; role?: string }) =>
      request<{ otpSent: boolean; devCode?: string; role?: string }>(`/api/services/user/register`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    verifyOtp: (body: { email: string; code: string; name: string; password: string; phone?: string; countryCode?: string; role?: string }) =>
      request<{ user: User }>(`/api/services/user/verify-otp`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Fast login — phone + countryCode (primary) OR email (fallback), with
    // password. No OTP: the server authenticates directly and returns the
    // user so the client can enter the app immediately.
    login: (body: {
      phone?: string;
      countryCode?: string;
      email?: string;
      password: string;
      rememberMe?: boolean;
      deviceFingerprint?: string;
    }) =>
      request<{ user: User }>(`/api/services/user/login`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    verifyLogin: (body: { email: string; code: string }) =>
      request<{ user: User }>(`/api/services/user/verify-login`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    logout: () => request<{ success: boolean }>(`/api/services/user/logout`, { method: "POST" }),
    me: () => request<{ user: User }>(`/api/services/user/me`),
    updateProfile: (body: { name?: string; phone?: string; language?: string; activeRole?: string; countryCode?: string }) =>
      request<{ user: User }>(`/api/services/user/me`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // Phase 2: phone verification.
    sendPhoneOtp: (body: { phone: string }) =>
      request<{ otpSent: boolean; devCode?: string }>(`/api/services/user/phone-otp`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    verifyPhone: (body: { code: string }) =>
      request<{ user: User }>(`/api/services/user/verify-phone`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Phase 5: auth flows.
    forgotPassword: (body: { email: string }) =>
      request<{ otpSent: boolean; devCode?: string }>(`/api/services/user/forgot-password`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    resetPassword: (body: { email: string; code: string; password: string }) =>
      request<{ success: boolean }>(`/api/services/user/reset-password`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    changePassword: (body: { currentPassword: string; newPassword: string }) =>
      request<{ success: boolean }>(`/api/services/user/change-password`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    refresh: () =>
      request<{ success: boolean }>(`/api/services/user/refresh`, { method: "POST" }),
    deleteAccount: (body: { confirmEmail: string }) =>
      request<{ success: boolean; anonymizedEmail?: string }>(`/api/services/user/delete-account`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    exportData: () =>
      request<{ bundle: Record<string, unknown>; url: string | null }>(`/api/services/user/export-data`, { method: "POST" }),
  },

  // ---- SESSIONS (Phase 5) ----
  sessions: {
    list: () =>
      request<{ sessions: any[]; devices: any[] }>(`/api/services/user/sessions`),
    revoke: (id: string) =>
      request<{ success: boolean }>(`/api/services/user/sessions/${id}`, {
        method: "DELETE",
      }),
    revokeAll: () =>
      request<{ success: boolean }>(`/api/services/user/sessions`, { method: "DELETE" }),
    verifyDevice: (body: { deviceId: string; code: string }) =>
      request<{ success: boolean }>(`/api/services/user/devices/verify`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    revokeDevice: (deviceId: string) =>
      request<{ success: boolean }>(`/api/services/user/devices/${deviceId}`, {
        method: "DELETE",
      }),
  },

  // ---- ADMIN AUDIT + TENANTS (Phase 5) ----
  adminAudit: {
    list: (params?: { action?: string; actorId?: string; limit?: number; offset?: number }) => {
      const q = new URLSearchParams();
      if (params?.action) q.set("action", params.action);
      if (params?.actorId) q.set("actorId", params.actorId);
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.offset) q.set("offset", String(params.offset));
      return request<{ logs: any[]; total: number }>(`/api/services/admin/audit-logs?${q.toString()}`);
    },
  },
  adminTenants: {
    list: () =>
      request<{ tenants: any[] }>(`/api/services/admin/tenants`),
    create: (body: { name: string; slug: string; plan?: string }) =>
      request<{ tenant: any }>(`/api/services/admin/tenants`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- JOB SERVICE ----
  jobs: {
    list: (params?: { status?: string; role?: string; vertical?: string; mode?: string }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      if (params?.role) q.set("role", params.role);
      if (params?.vertical) q.set("vertical", params.vertical);
      if (params?.mode) q.set("mode", params.mode);
      return request<{ jobs: DeliveryJob[] }>(`/api/services/job/list?${q.toString()}`);
    },
    get: (id: string) => request<{ job: DeliveryJob }>(`/api/services/job/${id}`),
    create: (body: any) =>
      request<{ job: DeliveryJob }>(`/api/services/job/create`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    transition: (id: string, body: { status: string; confirmationCode?: string; cancelReason?: string }) =>
      request<{ job: DeliveryJob }>(`/api/services/job/${id}/transition`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    cancel: (id: string, reason: string) =>
      request<{ job: DeliveryJob }>(`/api/services/job/${id}/transition`, {
        method: "POST",
        body: JSON.stringify({ status: "cancelled", cancelReason: reason }),
      }),
    // V1.0: instant delivery-fee quote (Pickup & Deliver fee calculator).
    quote: (body: {
      pickupLat?: number;
      pickupLng?: number;
      dropoffLat?: number;
      dropoffLng?: number;
      weightKg?: number;
      size?: "small" | "medium" | "large";
      scheduledFor?: string | null;
      mode?: "pickup_deliver" | "order_deliver";
    }) =>
      request<{ quote: QuoteBreakdown }>(`/api/services/job/quote`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // V1.0: courier earnings dashboard.
    earnings: () =>
      request<CourierEarnings>(`/api/services/job/earnings`),
    // Direct courier accept (no offer bargaining). Assigns the open job to the
    // calling courier at the customer's stated delivery fee (customerOffer).
    accept: (jobId: string) =>
      request<{ job: DeliveryJob }>(`/api/services/job/${jobId}/accept`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
  },

  // ---- STORE / MERCHANT SERVICE (V1.0 — Order & Deliver mode) ----
  stores: {
    list: (params?: { category?: string; q?: string; lat?: number; lng?: number }) => {
      const q = new URLSearchParams();
      if (params?.category) q.set("category", params.category);
      if (params?.q) q.set("q", params.q);
      if (params?.lat != null) q.set("lat", String(params.lat));
      if (params?.lng != null) q.set("lng", String(params.lng));
      return request<{ stores: Store[] }>(`/api/services/store/list?${q.toString()}`);
    },
    get: (id: string, params?: { lat?: number; lng?: number }) => {
      const q = new URLSearchParams();
      if (params?.lat != null) q.set("lat", String(params.lat));
      if (params?.lng != null) q.set("lng", String(params.lng));
      return request<{ store: Store }>(`/api/services/store/${id}?${q.toString()}`);
    },
    me: () =>
      request<{
        store: Store | null;
        boost?: StoreBoost | null;
        commissionTier?: string;
        tierExpiresAt?: string | null;
        commissionRate?: number;
        subscription?: Subscription | null;
        upgradeCalc?: UpgradeCalc;
      }>(`/api/services/store/me`),
    create: (body: {
      name: string;
      description?: string;
      category?: string;
      address?: string;
      lat?: number;
      lng?: number;
      imageUrl?: string;
      bannerUrl?: string;
      commissionTier?: "basic" | "premium" | "flagship";
    }) =>
      request<{ store: Store }>(`/api/services/store/me`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (body: Partial<{
      name: string;
      description: string | null;
      address: string | null;
      lat: number | null;
      lng: number | null;
      imageUrl: string | null;
      bannerUrl: string | null;
      active: boolean;
    }>) =>
      request<{ store: Store }>(`/api/services/store/me`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // Merchant product (menu item) management.
    products: () =>
      request<{ products: Product[] }>(`/api/services/store/product`),
    addProduct: (body: {
      name: string;
      description?: string;
      price: number;
      imageUrl?: string;
      category?: string;
      available?: boolean;
      discountedPrice?: number;
      sortOrder?: number;
    }) =>
      request<{ product: Product }>(`/api/services/store/product`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateProduct: (id: string, body: Partial<{
      name: string;
      description: string | null;
      price: number;
      imageUrl: string | null;
      category: string | null;
      available: boolean;
      discountedPrice: number | null;
      sortOrder: number;
    }>) =>
      request<{ product: Product }>(`/api/services/store/product/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteProduct: (id: string) =>
      request<{ success: boolean }>(`/api/services/store/product/${id}`, {
        method: "DELETE",
      }),
    // Customer places an order (cart → DeliveryJob mode=order_deliver).
    placeOrder: (body: {
      storeId: string;
      items: { productId: string; qty: number }[];
      dropoffAddress: string;
      dropoffLat?: number;
      dropoffLng?: number;
      dropoffNotes?: string;
      contactPhone?: string;
      scheduledFor?: string | null;
    }) =>
      request<{ job: DeliveryJob; quote: QuoteBreakdown; subtotal: number; total: number }>(
        `/api/services/store/order`,
        { method: "POST", body: JSON.stringify(body) }
      ),
    // Merchant dashboard analytics.
    dashboard: () =>
      request<{
        store: Store;
        stats: {
          totalOrders: number;
          todaysOrders: number;
          activeOrders: number;
          completedOrders: number;
          revenueToday: number;
          revenueWeek: number;
          commissionTotal: number;
          payoutTotal: number;
        };
        popularItems: { name: string; qty: number; revenue: number }[];
        recentOrders: DeliveryJob[];
      }>(`/api/services/store/dashboard`),
    // Merchant order management.
    orders: () =>
      request<{ orders: DeliveryJob[] }>(`/api/services/store/orders`),
    updateOrderStatus: (jobId: string, action: "confirm" | "preparing" | "ready") =>
      request<{ job: DeliveryJob }>(`/api/services/store/orders`, {
        method: "PATCH",
        body: JSON.stringify({ jobId, action }),
      }),
    // V1.0 Monetization: "Boost My Store" sponsored storefront.
    boost: () =>
      request<{ boost: StoreBoost | null; store: { id: string; name: string } | null }>(
        `/api/services/store/boost`
      ),
    startBoost: (dailyBudget: number) =>
      request<{ store: Store; boost: StoreBoost }>(`/api/services/store/boost`, {
        method: "POST",
        body: JSON.stringify({ dailyBudget }),
      }),
    stopBoost: () =>
      request<{ success: boolean }>(`/api/services/store/boost/stop`, {
        method: "POST",
      }),
  },

  // ---- JOB CHAT SERVICE ----
  // NOTE: The P2P offer/counter-offer bargaining module is DISABLED (business
  // model is now Pickup & Deliver + Order & Deliver with direct courier
  // assignment via api.jobs.accept). The job chat (messages) is retained so
  // customers and couriers can coordinate a delivery. The `negotiation` URL
  // prefix on messages is kept for backwards compatibility with the route.
  chat: {
    messages: {
      list: (jobId: string) =>
        request<{ messages: ChatMessage[] }>(`/api/services/negotiation/messages?jobId=${jobId}`),
      send: (body: { jobId: string; receiverId: string; content: string; type?: string; offerId?: string }) =>
        request<{ message: ChatMessage }>(`/api/services/negotiation/messages`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
    },
  },

  // ---- PAYMENT SERVICE ----
  payment: {
    get: (jobId: string) => request<{ payment: Payment | null }>(`/api/services/payment/${jobId}`),
    collect: (jobId: string, body: { confirmationCode: string }) =>
      request<{ payment: Payment }>(`/api/services/payment/${jobId}/collect`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    confirm: (jobId: string) =>
      request<{ payment: Payment }>(`/api/services/payment/${jobId}/confirm`, { method: "POST" }),
  },

  // ---- TRUST SERVICE ----
  trust: {
    me: () => request<{ trust: TrustRecord }>(`/api/services/trust/me`),
    submit: (body: { idType: string; idNumber: string; idFrontUrl: string; idBackUrl?: string; selfieUrl?: string }) =>
      request<{ trust: TrustRecord }>(`/api/services/trust/submit`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- ROLE APPLICATION SERVICE (multi-role: progressive verification) ----
  roleApplications: {
    list: () => request<{ applications: RoleApplication[] }>(`/api/services/role-application/list`),
    submit: (body: {
      role: "provider" | "courier";
      // provider docs
      businessName?: string;
      taxId?: string;
      storePhotoUrl?: string;
      bankingDetails?: string;
      // courier docs
      licenseNumber?: string;
      licensePhotoUrl?: string;
      vehicleInsuranceUrl?: string;
      vehiclePhotoUrl?: string;
      backgroundCheckUrl?: string;
      vehicleType?: string;
    }) =>
      request<{ application: RoleApplication }>(`/api/services/role-application/submit`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- WALLET SERVICE (multi-role: financial separation) ----
  wallet: {
    me: () => request<{ wallets: Wallet[] }>(`/api/services/wallet/me`),
    transactions: (type: "sender" | "courier" | "provider") =>
      request<{ transactions: WalletTransaction[] }>(`/api/services/wallet/transactions?type=${type}`),
    transfer: (body: { from: "courier" | "provider"; to: "sender"; amount: number }) =>
      request<{ fromWallet: Wallet; toWallet: Wallet }>(`/api/services/wallet/transfer`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- NOTIFICATION SERVICE ----
  notifications: {
    list: () => request<{ notifications: Notification[] }>(`/api/services/notification/list`),
    markRead: (id?: string) =>
      request<{ success: boolean }>(`/api/services/notification/read`, {
        method: "POST",
        body: JSON.stringify({ id }),
      }),
    subscribe: (body: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }) =>
      request<{ success: boolean }>(`/api/services/notification/subscribe`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- DISPUTE SERVICE ----
  disputes: {
    create: (body: { jobId: string; reason: string; description: string }) =>
      request<{ dispute: Dispute }>(`/api/services/negotiation/disputes`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- ADVERTISING (Phase 2) ----
  ads: {
    list: (params?: { vertical?: string; status?: string }) => {
      const q = new URLSearchParams();
      if (params?.vertical) q.set("vertical", params.vertical);
      if (params?.status) q.set("status", params.status);
      return request<{ ads: Advertisement[] }>(`/api/services/ads/list?${q.toString()}`);
    },
    // Public feed of active sponsored listings (shown to customers).
    feed: (params?: { vertical?: string }) => {
      const q = new URLSearchParams();
      if (params?.vertical) q.set("vertical", params.vertical);
      return request<{ ads: Advertisement[] }>(`/api/services/ads/feed?${q.toString()}`);
    },
    track: (body: { id: string; event: "impression" | "click" }) =>
      request<{ success: boolean }>(`/api/services/ads/track`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- ADMIN ----
  admin: {
    stats: () =>
      request<{ stats: any }>(`/api/services/admin/stats`),
    analytics: (params?: { range?: string }) => {
      const q = new URLSearchParams();
      if (params?.range) q.set("range", params.range);
      return request<{ analytics: any }>(`/api/services/admin/analytics?${q.toString()}`);
    },
    verifications: () =>
      request<{ items: any[] }>(`/api/services/admin/verifications`),
    reviewVerification: (id: string, body: { action: "approve" | "reject"; reason?: string }) =>
      request<{ trust: any }>(`/api/services/admin/verifications/${id}/review`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    disputes: () => request<{ items: Dispute[] }>(`/api/services/admin/disputes`),
    resolveDispute: (id: string, body: { resolution: string; finalStatus: string }) =>
      request<{ dispute: Dispute }>(`/api/services/admin/disputes/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    jobs: () => request<{ jobs: DeliveryJob[] }>(`/api/services/admin/jobs`),
    users: () => request<{ users: User[] }>(`/api/services/admin/users`),
    // Phase 2: advertising management.
    ads: () => request<{ ads: Advertisement[] }>(`/api/services/admin/ads`),
    createAd: (body: Partial<Advertisement>) =>
      request<{ ad: Advertisement }>(`/api/services/admin/ads`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateAd: (id: string, body: Partial<Advertisement>) =>
      request<{ ad: Advertisement }>(`/api/services/admin/ads/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteAd: (id: string) =>
      request<{ success: boolean }>(`/api/services/admin/ads/${id}`, { method: "DELETE" }),
    // Phase 2: commission rules management.
    commissionRules: () => request<{ rules: CommissionRule[] }>(`/api/services/admin/commission-rules`),
    createCommissionRule: (body: Partial<CommissionRule>) =>
      request<{ rule: CommissionRule }>(`/api/services/admin/commission-rules`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateCommissionRule: (id: string, body: Partial<CommissionRule>) =>
      request<{ rule: CommissionRule }>(`/api/services/admin/commission-rules/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteCommissionRule: (id: string) =>
      request<{ success: boolean }>(`/api/services/admin/commission-rules/${id}`, { method: "DELETE" }),
    // Phase 2: fraud flags review.
    fraudFlags: () => request<{ flags: FraudFlag[] }>(`/api/services/admin/fraud-flags`),
    resolveFraudFlag: (id: string, body: { status: "resolved" | "dismissed"; resolution: string }) =>
      request<{ flag: FraudFlag }>(`/api/services/admin/fraud-flags/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Phase 4: service status (enable/disable delivery verticals).
    serviceStatus: () => request<{ services: ServiceStatus[] }>(`/api/services/admin/services`),
    updateServiceStatus: (id: string, body: { enabled: boolean; reason?: string }) =>
      request<{ service: ServiceStatus }>(`/api/services/admin/services/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // V1.0: merchant onboarding — review store applications.
    stores: (params?: { status?: string }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      return request<{ stores: Store[] }>(`/api/services/admin/stores?${q.toString()}`);
    },
    reviewStore: (id: string, body: {
      action: "approve" | "reject" | "suspend";
      reason?: string;
      commissionTier?: "basic" | "premium" | "flagship";
    }) =>
      request<{ store: Store }>(`/api/services/admin/stores/${id}/review`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Multi-role: admin reviews provider/courier role applications.
    roleApplications: (params?: { status?: string }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      return request<{ applications: RoleApplication[] }>(`/api/services/admin/role-applications?${q.toString()}`);
    },
    reviewRoleApplication: (id: string, body: {
      action: "approve" | "reject";
      reason?: string;
    }) =>
      request<{ application: RoleApplication }>(`/api/services/admin/role-applications/${id}/review`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Phase 6: Multi-tenant — list tenants + tenant-scoped users.
    tenants: () => request<{ tenants: any[] }>(`/api/services/admin/tenants`),
    createTenant: (body: { name: string; slug: string; plan?: string }) =>
      request<{ tenant: any }>(`/api/services/admin/tenants`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    usersByTenant: (tenantId: string) =>
      request<{ users: User[]; tenantId: string }>(
        `/api/services/admin/users-by-tenant?tenantId=${encodeURIComponent(tenantId)}`
      ),
  },

  // ---- SERVICE STATUS (public) ----
  services: {
    status: () => request<{ services: ServiceStatus[] }>(`/api/services/status`),
  },

  // ---- SUBSCRIPTIONS (Phase 3) ----
  subscriptions: {
    plans: () => request<{ plans: SubscriptionPlan[] }>(`/api/services/subscriptions/plans`),
    mine: () => request<{ subscription: Subscription | null }>(`/api/services/subscriptions/me`),
    subscribe: (body: { planCode: string; billingCycle?: "monthly" | "annual" }) =>
      request<{ subscription: Subscription }>(`/api/services/subscriptions/subscribe`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    cancel: () =>
      request<{ subscription: Subscription }>(`/api/services/subscriptions/cancel`, {
        method: "POST",
      }),
  },

  // ---- MOBILE MONEY (Phase 3) ----
  mobileMoney: {
    list: () =>
      request<{ accounts: MobileMoneyAccount[] }>(`/api/services/mobile-money/accounts`),
    link: (body: { provider: MobileMoneyProvider; accountNumber: string; accountName?: string; pin: string }) =>
      request<{ account: MobileMoneyAccount }>(`/api/services/mobile-money/accounts`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    unlink: (id: string) =>
      request<{ success: boolean }>(`/api/services/mobile-money/accounts/${id}`, {
        method: "DELETE",
      }),
    transactions: (params?: { limit?: number }) => {
      const q = new URLSearchParams();
      if (params?.limit) q.set("limit", String(params.limit));
      return request<{ transactions: MobileMoneyTransaction[] }>(
        `/api/services/mobile-money/transactions?${q.toString()}`
      );
    },
    pay: (body: { jobId: string; provider: MobileMoneyProvider; pin: string }) =>
      request<{ transaction: MobileMoneyTransaction; payment: Payment }>(`/api/services/mobile-money/pay`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- CHAT TRANSLATION (Phase 3) ----
  translate: {
    translate: (body: { messageId: string; targetLang: string }) =>
      request<{ message: ChatMessage }>(`/api/services/translate`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- OFFLINE SYNC (Phase 3) ----
  sync: {
    push: (entries: { clientOpId: string; entity: string; op: string; payload: unknown }[]) =>
      request<{ applied: string[] }>(`/api/services/sync`, {
        method: "POST",
        body: JSON.stringify({ entries }),
      }),
  },

  // ---- FULL-TEXT SEARCH (Phase 6) ----
  search: {
    query: (params: { q: string; type?: "all" | "jobs" | "stores" | "products" | "users"; limit?: number }) => {
      const q = new URLSearchParams({ q: params.q });
      if (params.type) q.set("type", params.type);
      if (params.limit) q.set("limit", String(params.limit));
      return request<{
        results: {
          jobs: any[];
          stores: any[];
          products: any[];
          users: any[];
        };
        query: string;
      }>(`/api/services/search?${q.toString()}`);
    },
  },

  // ---- REVIEWS & RATINGS (Phase 6) ----
  reviews: {
    list: (params: { jobId?: string; targetId?: string; targetType?: string; reviewerId?: string }) => {
      const q = new URLSearchParams();
      if (params.jobId) q.set("jobId", params.jobId);
      if (params.targetId) q.set("targetId", params.targetId);
      if (params.targetType) q.set("targetType", params.targetType);
      if (params.reviewerId) q.set("reviewerId", params.reviewerId);
      return request<{ reviews: any[] }>(`/api/services/review/list?${q.toString()}`);
    },
    create: (body: { jobId: string; targetType: "courier" | "customer" | "store"; rating: number; comment?: string }) =>
      request<{ review: any }>(`/api/services/review/create`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- EMAIL TEMPLATE PREVIEW (Phase 6, admin-only) ----
  email: {
    previewUrl: (template: string) =>
      `/api/services/email/preview?template=${encodeURIComponent(template)}`,
  },
};
